import { defineStore } from 'pinia'
import _ from 'lodash'

import { resourceApi } from './common/api'

const menusApi = resourceApi.extend('menus')

export const useLayout = defineStore('layout', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: ['currentTheme', 'page', 'homePage', 'pageView', 'rail'],
      storage: localStorage
    }
  ],

  state() {
    return {
      appTitle: 'Portal',
      currentTheme: 'dark',
      pageView: {},
      pageViewBtn: null,
      pages: [
        {
          title: 'Home',
          icon: 'mdi-home-outline'
        },
        {
          title: 'Users',
          icon: 'mdi-account-multiple-outline',
          name: 'Users',
          view: false,
          show: {
            store: {
              id: 'auth',
              getter: 'isInstructor'
            },
            expected: [false]
          },
          hide: {
            store: {
              id: 'auth',
              getter: 'isLearner'
            },
            expected: [true]
          },
          button: {
            label: 'Invite',
            icon: 'mdi-plus',
            click: {
              dialog: true,
              component: '@/components/forms/Invite.vue'
            },
            visible: {
              store: {
                id: 'auth',
                getter: 'isAdmin'
              }
            }
          }
        },
        {
          title: 'Categories',
          icon: 'mdi-file-tree-outline',
          name: 'Categories',
          view: false,
          button: false,
          hide: {
            store: {
              id: 'auth',
              getter: 'isLearner'
            },
            expected: [true]
          }
        },
        {
          title: 'Analytics',
          icon: 'mdi-chart-box-outline',
          name: 'Analytics',
          view: false,
          button: false,
          hide: {
            store: {
              id: 'auth',
              getter: 'isDeveloper'
            },
            expected: [true]
          }
        },
        {
          title: 'Demo links',
          icon: 'mdi-link-variant',
          name: 'DemoLinks',
          view: false,
          button: false,
          show: {
            store: {
              id: 'auth',
              getter: 'isSuperAdmin'
            },
            expected: [true]
          }
        },
        {
          title: 'Demo sites',
          icon: 'mdi-web',
          name: 'DemoSites',
          view: false,
          button: false,
          show: {
            store: {
              id: 'auth',
              getter: 'isSuperAdmin'
            },
            expected: [true]
          }
        },
        {
          title: 'Access Roster',
          icon: 'mdi-account-key-outline',
          name: 'AccessRoster',
          view: false,
          show: {
            store: {
              id: 'auth',
              getter: 'canSeeAccessRoster'
            },
            expected: [true]
          },
          button: {
            label: 'Add learners',
            icon: 'mdi-plus',
            click: {
              dialog: true,
              component: '@/components/forms/DirectAccessInvite.vue'
            }
          }
        },
        {
          title: 'Institutions',
          icon: 'mdi-school-outline',
          name: 'Institutions',
          common: false,
          view: false,
          show: {
            store: {
              id: 'auth',
              getter: 'isSuperAdmin'
            },
            expected: [true]
          },
          button: {
            label: 'Create',
            icon: 'mdi-plus',
            click: {
              dialog: true,
              component: '@/components/adm/forms/Institution.vue'
            }
          }
        },
        {
          title: 'Licenses',
          value: 'licenses',
          icon: 'mdi-key-chain',
          name: 'Licenses',
          common: false,
          view: false,
          show: {
            store: {
              id: 'auth',
              getter: 'isSuperAdmin'
            },
            expected: [true]
          },
          button: {
            label: 'Create',
            icon: 'mdi-plus',
            click: {
              dialog: true,
              component: '@/components/adm/forms/License.vue'
            }
          }
        },
        {
          title: 'VR Simulations',
          name: 'Simulations',
          icon: {
            menu: 'mdi-virtual-reality',
            view: {
              store: {
                id: 'simulation',
                getter: 'getMenuIcon',
                action: 'toggleIsViewSimulation',
                readonly: {
                  store: {
                    getter: 'getIsListSimulations'
                  }
                }
              }
            }
          },
          button: {
            menu: {
              label: 'Create',
              icon: 'mdi-plus',
              click: {
                dialog: true,
                component: '@/components/forms/Simulation.vue'
              }
            },
            view: [
              {
                icon: 'mdi-pencil-outline',
                label: 'Edit',
                visible: {
                  store: {
                    id: 'simulation',
                    getter: 'getActionsActiveSimulation'
                  }
                },
                callback: {
                  store: {
                    id: 'simulation',
                    action: 'activateIsEditSimulation'
                  }
                }
              },
              {
                icon: 'mdi-package-down',
                label: 'Archive',
                visible: {
                  store: {
                    id: 'simulation',
                    getter: 'getActionsActiveSimulation'
                  }
                },
                callback: {
                  store: {
                    id: 'simulation',
                    action: 'activateIsArchiveSimulation'
                  }
                }
              },
              {
                icon: 'mdi-restore',
                label: 'Restore',
                visible: {
                  store: {
                    id: 'simulation',
                    getter: 'getActionsArchivedSimulation'
                  }
                },
                callback: {
                  store: {
                    id: 'simulation',
                    action: 'activateIsRestoreSimulation'
                  }
                }
              }
            ]
          }
        },
        {
          title: 'Screen-based Simulations',
          name: 'SimulationPlay',
          view: false,
          button: false,
          icon: 'mdi-television-play'
        },
        {
          title: 'Scenario Authoring',
          name: 'ScenarioAuthoring',
          view: false,
          button: false,
          icon: 'mdi-fountain-pen-tip',
          show: {
            store: {
              id: 'auth',
              getter: 'canAuthorScenarios'
            },
            expected: [true]
          }
        },
        {
          title: 'Scenario Approvals',
          name: 'ScenarioApprovals',
          view: false,
          button: false,
          icon: 'mdi-clipboard-check-outline',
          show: {
            store: {
              id: 'auth',
              getter: 'canApproveScenarios'
            },
            expected: [true]
          }
        },
        {
          title: 'Screen-based access',
          name: 'ScreenBasedAccess',
          view: false,
          button: false,
          icon: 'mdi-shield-key-outline',
          show: {
            store: {
              id: 'auth',
              getter: 'isAdmin'
            },
            expected: [true]
          }
        },
        {
          title: 'Authoring permissions',
          name: 'AuthoringPermissions',
          common: false,
          view: false,
          button: false,
          icon: 'mdi-account-edit-outline',
          show: {
            store: {
              id: 'auth',
              getter: 'isSuperAdmin'
            },
            expected: [true]
          }
        },
        {
          title: 'Resources',
          icon: 'mdi-folder-text-outline',
          name: 'Resources',
          view: false,
          show: {
            store: {
              id: 'auth',
              getter: 'isLearner'
            },
            expected: [false]
          },
          button: {
            label: 'Create',
            icon: 'mdi-plus',
            click: {
              dialog: true,
              component: '@/components/forms/Resource.vue'
            }
          }
        },
        {
          title: 'Bundles',
          common: false,
          view: false,
          show: {
            store: {
              id: 'auth',
              getter: 'isDeveloper'
            },
            expected: [true]
          },
          button: {
            label: 'Upload',
            icon: 'mdi-upload-box',
            click: {
              dialog: true,
              component: '@/components/dev/forms/Bundle.vue'
            }
          },
          icon: 'mdi-package-variant',
          name: 'Bundles'
        },
        {
          title: 'Assessments',
          view: false,
          button: false,
          hide: {
            store: {
              id: 'auth',
              getter: 'isDeveloper'
            },
            expected: [true]
          },
          name: 'SimulationReports',
          value: 'simulationReports',
          icon: {
            menu: 'mdi-file-chart',
            view: {
              store: {
                id: 'simulationRun',
                getter: 'getMenuIcon',
                action: 'toggleView',
                readonly: {
                  store: {
                    getter: 'getIsListView'
                  }
                }
              }
            }
          }
        },
        {
          title: 'Feedback',
          view: false,
          button: false,
          icon: 'mdi-account-voice',
          name: 'Feedback'
        }
      ],
      footers: [
        {
          store: {
            id: 'auth',
            getter: 'getUserName'
          },
          avatar: {
            store: {
              id: 'auth',
              getter: 'getAvatar'
            }
          },
          items: [
            {
              title: 'Log Out',
              value: 'logout',
              icon: 'mdi-logout',
              store: {
                id: 'auth',
                action: 'logout'
              }
            }
          ]
        },
        {
          store: {
            id: 'auth',
            getter: 'getRoleName'
          },
          tooltip: false,
          value: 'viewAs',
          icon: 'mdi-shield-account-variant',
          hide: {
            store: {
              id: 'user',
              getter: 'getUserRoles'
            },
            expected: [{}]
          },
          color: {
            store: {
              id: 'auth',
              getter: 'getRoleColors'
            }
          },
          openOnHover: true,
          itemTitle: 'name',
          itemValue: 'code',
          itemCurrent: 'current',
          items: {
            store: {
              id: 'user',
              getter: 'getUserRoles',
              original: true
            },
            callback: {
              store: {
                id: 'auth',
                action: 'setRole'
              },
              args: {
                type: 'item',
                key: 'code'
              }
            }
          }
        },
        {
          title: {
            dark: 'Light Mode',
            light: 'Dark Mode'
          },
          store: {
            id: 'layout',
            action: 'toggleTheme'
          },
          icon: {
            dark: 'mdi-white-balance-sunny',
            light: 'mdi-weather-night'
          },
          value: 'mode'
        }
      ],
      page: null,
      homePage: null,
      cachedPage: null,
      currentPages: null,
      isReloadingPage: false,
      footer: null,
      footerItem: null,
      showFooterMenu: {},
      rail: true,
      menus: null,
      canAuthorScenarios: false,
      canApproveScenarios: false,
      isLoadingMenus: true,
      directAccessEnabled: false
    }
  },

  getters: {
    getMenus: ({ menus }) => menus,
    getIsLoadingMenus: ({ isLoadingMenus }) => isLoadingMenus,
    getDirectAccessEnabled: ({ directAccessEnabled }) => !!directAccessEnabled,
    getHomePage: ({ homePage }) => {
      return homePage
    },
    getCurrentPages: ({ currentPages }) => {
      return currentPages
    },
    getPageView: ({ pageView }) => {
      return pageView
    },
    getPageViewBtn: ({ pageViewBtn }) => {
      return pageViewBtn
    },
    getRail: ({ rail }) => {
      return rail
    },
    getCurrentTheme: ({ currentTheme }) => {
      return currentTheme
    },
    getTextShade: ({ currentTheme }) => {
      return currentTheme == 'dark' ? 'lighten' : 'darken'
    },
    getPages: ({ pages }) => pages,
    getFooters: ({ footers }) => footers
  },

  actions: {
    toggleTheme() {
      this.currentTheme = this.currentTheme == 'dark' ? 'light' : 'dark'
    },
    getMenuItems(menuItems, stores) {
      const setStoreMenuItems = (menuItems, stores) => {
        if (menuItems.length) {
          const storeGetter = (item, stores, isTitle = true) => {
            const store = stores[item.store.id]
            if (
              _.intersection(['state', 'getter'], _.keys(item.store)).length
            ) {
              if (isTitle)
                item.title = store[item.store.getter ?? item.store.state]
              else {
                let storeValue = store[item.store.getter ?? item.store.state]
                if (!_.isObjectLike(storeValue)) {
                  if (_.isString(storeValue) && _.has(storeValue, 'replace'))
                    storeValue = storeValue
                      .replace(/^\s+|\s+$/g, '')
                      .toLowerCase()
                  storeValue = { value: storeValue }
                }
                const callback =
                  _.has(item, 'callback') &&
                  storeValue != null &&
                  !_.isEmpty(storeValue) &&
                  (!_.isPlainObject(storeValue) ||
                    _.keys(storeValue).length > 1 ||
                    !_.has(storeValue, 'value') ||
                    storeValue.value != null)
                    ? _.isPlainObject(item.callback) &&
                      _.has(item.callback, 'store') &&
                      _.has(item.callback.store, 'action')
                      ? stores[item.callback.store.id][
                          item.callback.store.action
                        ]
                      : item.callback
                    : undefined
                const args =
                  _.has(item, 'callback') &&
                  _.has(item.callback, 'args') &&
                  item.callback.args != null &&
                  storeValue != null &&
                  !_.isEmpty(storeValue) &&
                  (!_.isPlainObject(storeValue) ||
                    _.keys(storeValue).length > 1 ||
                    !_.has(storeValue, 'value') ||
                    storeValue.value != null)
                    ? item.callback.args
                    : undefined
                if (
                  callback != null &&
                  _.isArrayLikeObject(storeValue) &&
                  _.isPlainObject(storeValue[0])
                )
                  storeValue = _.map(storeValue, (v) => {
                    let returnValue = {
                      ...v,
                      callback
                    }
                    if (args != null) returnValue.args = args
                    return returnValue
                  })
                item =
                  _.has(item.store, 'original') && item.store.original == true
                    ? storeValue
                    : callback != null
                      ? {
                          ...item,
                          ...storeValue,
                          callback
                        }
                      : {
                          ...item,
                          ...storeValue
                        }
              }
            } else if (_.has(item.store, 'action')) {
              item.callback = store[item.store.action]
            }
            delete item.store
            return item
          }
          _.map(menuItems, (item) => {
            if (_.has(item, 'store')) {
              item = storeGetter(item, stores)
            }
            for (const k of ['avatar', 'items', 'color'])
              if (_.has(item, k) && _.has(item[k], 'store'))
                item[k] = storeGetter(item[k], stores, false)
            for (const k of ['show', 'hide'])
              if (_.has(item, k) && _.has(item[k], 'store')) {
                let storeItem = storeGetter(item[k], stores, false)
                delete storeItem.expected
                item[k] = item[k].expected.some((expected) => {
                  const _storeItemValue = _.has(storeItem, 'value')
                    ? storeItem.value
                    : storeItem
                  if (_.isNaN(expected) && _.isNaN(_storeItemValue)) return true
                  return _.isEqual(_storeItemValue, expected)
                })
              }
            if (_.has(item, 'props')) {
              _.map(_.keys(item.props), (p) => {
                if (_.has(item.props[p], 'store'))
                  item.props[p] = storeGetter(item.props[p], stores, false)
                delete item.props[p].value
              })
            }
            if (
              _.has(item, 'button') &&
              _.has(item.button, 'click') &&
              _.has(item.button.click, 'props')
            ) {
              _.map(_.keys(item.button.click.props), (p) => {
                if (_.has(item.button.click.props[p], 'store'))
                  item.button.click.props[p] = storeGetter(
                    item.button.click.props[p],
                    stores,
                    false
                  )
                delete item.button.click.props[p].value
              })
            }
            if (_.has(item, 'items')) setStoreMenuItems(item.items, stores)
            return item
          })
        }
        return menuItems
      }
      const getItem = (item) => {
        if (!_.has(item, 'value') && _.has(item, 'title'))
          item.value = item.title.trim().toLowerCase()
        const title =
          !_.has(item, 'title') || item.title == null
            ? undefined
            : menuItems == 'getFooters' &&
                _.isPlainObject(item.title) &&
                !_.isEmpty(item.title)
              ? item.title[this.currentTheme]
              : item.title
        const icon =
          !_.has(item, 'icon') || item.icon == null
            ? menuItems == 'getFooters' &&
              _.has(item, 'avatar') &&
              _.isPlainObject(item.avatar) &&
              !_.isEmpty(item.avatar)
              ? undefined
              : 'mdi-circle-medium'
            : menuItems == 'getFooters' &&
                _.isPlainObject(item.icon) &&
                !_.isEmpty(item.icon)
              ? item.icon[this.currentTheme]
              : item.icon
        const _class = 'my-1'
        return { ...item, title, icon, class: _class }
      }
      if (menuItems != null && menuItems != '') {
        menuItems = _.map(_.cloneDeep(this[menuItems]), (m) => {
          m = getItem(m)
          if (_.has(m, 'items')) {
            _.map(m.items, (i) => getItem(i))
          }
          return m
        })
        return _.filter(
          setStoreMenuItems(menuItems, stores),
          (m) => {
            if (this.isLoadingMenus) return m.value == 'home'
            if (m?.hide || (_.has(m, 'show') && m.show !== true)) return false
            if (m.name === 'AccessRoster' && stores?.auth?.isSuperAdmin) return true
            const instMenus = this.menus ?? {}
            return _.has(instMenus, m.name) ? instMenus[m.name] : true
          }
        )
      } else return null
    },
    toggleFooterMenu(key) {
      this.showFooterMenu[key] = !this.showFooterMenu[key]
    },
    resetFooter() {
      this.footer = null
      this.footerItem = null
    },
    setMenu(
      currentMenu,
      clickedItem,
      defaultItem = null,
      parentItem = null
    ) {
      if (currentMenu == 'page') {
        this.resetFooter()
        if (
          this[currentMenu] != null &&
          defaultItem == null &&
          this[currentMenu]?.value == clickedItem.value
        )
          this.isReloadingPage = true
      } else if (currentMenu == 'footer') this.footerItem = null
      this[currentMenu] =
        this[currentMenu] != null &&
        defaultItem != null &&
        this[currentMenu]?.value == clickedItem.value
          ? defaultItem
          : clickedItem
      if (parentItem != null) this[parentItem] = null
      if (_.has(this[currentMenu], 'callback')) {
        const args =
          _.has(clickedItem, 'args') &&
          clickedItem.args != null &&
          _.isPlainObject(clickedItem.args) &&
          _.has(clickedItem.args, 'type') &&
          _.has(clickedItem.args, 'key') &&
          clickedItem.args.type == 'item' &&
          _.has(clickedItem, clickedItem.args.key)
            ? [clickedItem[clickedItem.args.key]]
            : [undefined]
        this[currentMenu].callback(...args)
      }
      if (currentMenu == 'page') {
        if (
          this[currentMenu] != null &&
          this[currentMenu].value != 'home' &&
          (this[currentMenu]?.view == null || this[currentMenu].view)
        ) {
          if (
            _.isEmpty(this.pageView) ||
            _.isEmpty(this.pageView?.[this[currentMenu].value])
          )
            this.pageView[this[currentMenu].value] = 'grid'
          this.pageViewBtn = this.pageView[this[currentMenu].value]
        }
        if (this.router.currentRoute.value.path != '/') {
          this.cachedPage = this.page
          this.router.push('/')
        }
      }
    },
    resetStore(isAuthenticated) {
      this.footer = null
      this.footerItem = null
      this.showFooterMenu = {}
      if (!isAuthenticated) this.page = null
    },
    setHomePage(page) {
      this.homePage = page
    },
    setPage(page) {
      this.page = page
    },
    setCurrentPages(pages) {
      this.currentPages = pages
    },
    setPageView(pageKey, newValue) {
      this.pageView[pageKey] = newValue
    },
    async fetchMenus() {
      this.isLoadingMenus = true
      try {
        const result = await menusApi.get('')
        this.menus = result.data?.response?.menus ?? null
        this.directAccessEnabled = !!result.data?.response?.directAccessEnabled
        this.canAuthorScenarios = !!result.data?.response?.canAuthorScenarios
        this.canApproveScenarios = !!result.data?.response?.canApproveScenarios
        try {
          const { useAuthentication } = await import('./auth')
          const auth = useAuthentication()
          auth.setCanAuthorScenarios(this.canAuthorScenarios)
          auth.setCanApproveScenarios(this.canApproveScenarios)
        } catch {
          /* auth store may not be ready during early boot */
        }
      } catch {
        this.menus = null
        this.directAccessEnabled = false
        this.canAuthorScenarios = false
        this.canApproveScenarios = false
      } finally {
        this.isLoadingMenus = false
      }
    }
  }
})
